import type {
  ReorderBestEffortSummary,
  ReorderDiagnostic,
  ReorderDiagnosticCode,
  ReorderPipelineFailureCode,
  ReorderPresentation,
} from "@/domain/reorder/reorder.types";
import type { CartValidityViewModel } from "@/domain/cart/cart-validity.types";

type PipelineFailurePresentationCode = Exclude<
  ReorderPipelineFailureCode,
  | "MISSING_MENU_ITEM_ID"
  | "MENU_ITEM_NOT_IN_CATALOG"
  | "MODIFIER_WIRING_MISSING"
  | "MODIFIER_GROUP_NOT_IN_CATALOG"
  | "MODIFIER_NOT_IN_CATALOG"
  | "REQUIRED_MODIFIER_INCOMPLETE"
  | "CATEGORY_FALLBACK"
  | "COMBO_NOT_SUPPORTED"
  | "QUANTITY_REDUCED"
>;

const FAILURE_COPY: Record<
  PipelineFailurePresentationCode,
  Pick<ReorderPresentation, "headline" | "primaryAction">
> = {
  CATALOG_NOT_LOADED: {
    headline: "The menu isn't available right now. Please try again.",
    primaryAction: "refresh_menu",
  },
  RESTAURANT_MISMATCH: {
    headline: "This order belongs to a different restaurant",
    primaryAction: "browse_menu",
  },
  RESTAURANT_UNAVAILABLE: {
    headline: "Restaurant is not accepting orders",
    primaryAction: "refresh_menu",
  },
  EMPTY_ORDER: {
    headline: "This order has no items to reorder",
    primaryAction: null,
  },
  NOTHING_RESTORED: {
    headline: "This order can't be reordered because the menu has changed.",
    primaryAction: "browse_menu",
  },
  TRANSFORM_FAILED: {
    headline: "This order can't be reordered because the menu has changed.",
    primaryAction: "browse_menu",
  },
  VALIDITY_UNKNOWN: {
    headline: "We're still verifying the menu. Please try again in a moment.",
    primaryAction: "refresh_menu",
  },
  VALIDITY_INVALID: {
    headline: "This order can't be reordered because the menu has changed.",
    primaryAction: "browse_menu",
  },
  VALIDITY_RESTAURANT_UNAVAILABLE: {
    headline: "Restaurant is not accepting orders",
    primaryAction: "refresh_menu",
  },
};

export type ReorderFailureAnalyticsPayload = {
  orderId: string;
  failure_code: string;
  primary_diagnostic: ReorderDiagnosticCode | null;
  diagnostic_count: number;
};

export function buildReorderFailureAnalyticsPayload(
  orderId: string,
  failureCode: string,
  diagnostics: ReorderDiagnostic[] = []
): ReorderFailureAnalyticsPayload {
  return {
    orderId,
    failure_code: failureCode,
    primary_diagnostic: diagnostics[0]?.code ?? null,
    diagnostic_count: diagnostics.length,
  };
}

/** Runtime-only copy when catalog fetch fails before pipeline evaluation. */
export function deriveCatalogFetchFailurePresentation(): ReorderPresentation {
  return {
    headline: "Couldn't load the menu. Check your connection and try again.",
    primaryAction: "refresh_menu",
  };
}

/**
 * Customer-facing failure copy. Never includes validator names, IDs, or exception details.
 */
export function deriveReorderFailurePresentation(
  code: ReorderPipelineFailureCode,
  _diagnostics: ReorderDiagnostic[] = [],
  validity?: CartValidityViewModel | null
): ReorderPresentation {
  if (code === "VALIDITY_INVALID" || code === "NOTHING_RESTORED" || code === "TRANSFORM_FAILED") {
    return { ...FAILURE_COPY.NOTHING_RESTORED };
  }

  const base = FAILURE_COPY[code as PipelineFailurePresentationCode];
  if (!base) {
    return { ...FAILURE_COPY.NOTHING_RESTORED };
  }

  const validityHeadline = validity?.presentation.headline?.trim();
  if (code.startsWith("VALIDITY_") && validityHeadline) {
    return {
      headline: validityHeadline,
      primaryAction: base.primaryAction,
    };
  }

  return {
    headline: base.headline,
    primaryAction: base.primaryAction,
  };
}

function formatSkippedItemsHeadline(count: number): string {
  if (count === 1) {
    return "1 item is currently unavailable and was not added to your cart.";
  }
  return `${count} items are currently unavailable and were not added to your cart.`;
}

/**
 * Customer-facing success copy for exact or partial restore.
 * Prefer customization messaging when both customizations and items changed.
 */
export function deriveReorderSuccessPresentation(
  summary?: ReorderBestEffortSummary
): ReorderPresentation {
  if (!summary) {
    return {
      headline: "Order is ready to add to your cart",
      primaryAction: null,
    };
  }

  const removedCount = summary.removedCustomizations.length;
  const skippedCount = summary.skippedItems.length;
  const lineNeedsReview = summary.addedItems.some((line) => line.needsReview === true);

  // Prefer customization review copy when both customizations and items changed.
  if (removedCount > 0 || lineNeedsReview) {
    return {
      headline:
        "We restored most of your order. A few customizations are no longer available.",
      primaryAction: "review_items",
    };
  }

  if (skippedCount > 0) {
    return {
      headline: formatSkippedItemsHeadline(skippedCount),
      primaryAction: "browse_menu",
    };
  }

  return {
    headline: "Order is ready to add to your cart",
    primaryAction: null,
  };
}

/** True when success presentation should surface a cart/checkout banner. */
export function shouldSurfaceReorderReviewBanner(
  presentation: ReorderPresentation
): boolean {
  return (
    presentation.primaryAction === "review_items" ||
    presentation.primaryAction === "browse_menu"
  );
}
